import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, Copy, Check, Lock, Terminal, Trash2 } from 'lucide-react';
import { useLogsModal } from '../../context/LogsModalContext';
import { useToast } from '../../context/ToastContext';
import { getLogNames, getLogContent, clearLog } from '../../api/logs';
import { getErrorMessage } from '../../lib/utils';
import ConfirmDialog from '../ui/ConfirmDialog';

const LEVEL_LINE_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s+(DEBUG|INFO|WARNING|ERROR|CRITICAL)\b/;

const LEVEL_STYLES = {
  error: 'text-red-400',
  warning: 'text-amber-400',
  default: 'text-green-400',
};

function classifyLines(content) {
  let mode = 'default';
  return content.split('\n').map((text) => {
    const match = text.match(LEVEL_LINE_RE);
    if (match) {
      const level = match[1];
      mode = level === 'ERROR' || level === 'CRITICAL' ? 'error' : level === 'WARNING' ? 'warning' : 'default';
    }
    return { text, mode };
  });
}

export default function LogsModal() {
  const { isOpen, close } = useLogsModal();
  const { addToast } = useToast();

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [sessionPassword, setSessionPassword] = useState(null);

  const [logNames, setLogNames] = useState([]);
  const [selectedLog, setSelectedLog] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const passwordInputRef = useRef(null);

  const lines = useMemo(() => classifyLines(content), [content]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => passwordInputRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Fully resets on close — the password gate is asked again every time,
  // not just cached for the browser session.
  function handleClose() {
    close();
    setPassword('');
    setPasswordError('');
    setSessionPassword(null);
    setLogNames([]);
    setSelectedLog('');
    setContent('');
  }

  async function loadContent(name, pwd) {
    setLoading(true);
    try {
      const { data } = await getLogContent(name, pwd);
      setContent(data.content || '(empty log)');
    } catch (err) {
      addToast(getErrorMessage(err, 'Failed to load log content'), 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleUnlock(e) {
    e.preventDefault();
    if (!password) return;
    setUnlocking(true);
    setPasswordError('');
    try {
      const { data } = await getLogNames(password);
      const names = data.logs || [];
      if (names.length === 0) {
        setPasswordError('No log files found on the server yet.');
        return;
      }
      setSessionPassword(password);
      setLogNames(names);
      setSelectedLog(names[0]);
      await loadContent(names[0], password);
    } catch (err) {
      setPasswordError(
        err.response?.status === 403 ? 'Incorrect password.' : getErrorMessage(err, 'Could not verify password')
      );
    } finally {
      setUnlocking(false);
    }
  }

  function handleRefresh() {
    if (sessionPassword && selectedLog) loadContent(selectedLog, sessionPassword);
  }

  function handleSwitchLog(name) {
    setSelectedLog(name);
    if (sessionPassword) loadContent(name, sessionPassword);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      addToast('Could not copy — clipboard access denied', 'error');
    }
  }

  async function handleClear() {
    if (!sessionPassword || !selectedLog) return;
    setClearing(true);
    try {
      await clearLog(selectedLog, sessionPassword);
      setContent('');
      addToast(`${selectedLog}.log cleared`, 'success');
      setClearConfirmOpen(false);
    } catch (err) {
      addToast(getErrorMessage(err, 'Failed to clear log'), 'error');
    } finally {
      setClearing(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl" style={{ height: sessionPassword ? '80vh' : 'auto' }}>
        {!sessionPassword ? (
          <div className="flex flex-col items-center gap-4 px-8 py-10">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15">
              <Lock size={22} className="text-amber-400" />
            </div>
            <div className="text-center">
              <h2 className="text-base font-semibold text-gray-100">Server Logs</h2>
              <p className="mt-1 text-sm text-gray-400">Enter the logs password to continue.</p>
            </div>
            <form onSubmit={handleUnlock} className="w-full max-w-xs space-y-3">
              <input
                ref={passwordInputRef}
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                placeholder="Logs password"
                className="w-full rounded-xl border border-gray-700 bg-gray-800 px-4 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              {passwordError && <p className="text-xs text-red-400">{passwordError}</p>}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 rounded-xl border border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!password || unlocking}
                  className="flex-1 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-amber-400 disabled:opacity-50"
                >
                  {unlocking ? 'Checking…' : 'Unlock'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <div className="flex shrink-0 items-center gap-2 border-b border-gray-700 bg-gray-800/60 px-4 py-3">
              <Terminal size={16} className="text-amber-400 shrink-0" />
              <h2 className="text-sm font-semibold text-gray-100 shrink-0">Server Logs</h2>

              {logNames.length > 1 ? (
                <select
                  value={selectedLog}
                  onChange={(e) => handleSwitchLog(e.target.value)}
                  className="ml-2 rounded-lg border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {logNames.map((name) => (
                    <option key={name} value={name}>{name}.log</option>
                  ))}
                </select>
              ) : (
                <span className="ml-2 rounded-lg bg-gray-800 px-2.5 py-1 text-xs text-gray-400">
                  {selectedLog ? `${selectedLog}.log` : ''}
                </span>
              )}

              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-gray-100 disabled:opacity-50"
                  aria-label="Refresh log"
                  title="Refresh"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={handleCopy}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-gray-100"
                  aria-label="Copy log contents"
                  title="Copy contents"
                >
                  {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                </button>
                <button
                  onClick={() => setClearConfirmOpen(true)}
                  disabled={!content}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-900/40 hover:text-red-400 disabled:opacity-50"
                  aria-label="Clear log"
                  title="Clear log"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  onClick={handleClose}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-gray-100"
                  aria-label="Close"
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-black px-4 py-3">
              {loading && !content ? (
                <p className="text-xs text-gray-500">Loading…</p>
              ) : (
                <pre className="select-text whitespace-pre-wrap break-all font-mono text-xs leading-relaxed">
                  {lines.map((line, i) => (
                    <div key={i} className={LEVEL_STYLES[line.mode]}>
                      {line.text || ' '}
                    </div>
                  ))}
                </pre>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        onConfirm={handleClear}
        title="Clear log"
        message={`This permanently empties ${selectedLog}.log on the server. This can't be undone.`}
        confirmLabel="Clear"
        confirmVariant="danger"
        loading={clearing}
      />
    </div>,
    document.body
  );
}
